import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import notification from "@app/services/notification";
import {
    usePerformBoOperationMutation,
    type PerformBoOperationVariables,
} from "@src/businessModules/WorkShop/shared/bo/usePerformBoOperationMutation";
import {
    stockGatheringsServices,
    type StockGatheringCompleteReserveParams,
    type StockGatheringFinishComplectParams,
    type StockGatheringFinishComplectResult,
} from "../../../../api/stockGatherings";

/** Параметры хука мутаций карточки комплектования КВ. */
export interface UseStockGatheringCompletionCardMutationsParams {
    /**
     * Колбэк синхронизации обновлённого резерва после отпуска.
     * @param params код резерва и строки состава
     */
    onReserveRefetch?: (params: { reserveCode: number; gatheringContCode: number }) => void | Promise<unknown>;
    /** Колбэк успешной BO-операции или завершения комплектования. */
    onBoOperationSuccess?: () => void;
}

/** Мутации карточки комплектования КВ. */
export interface UseStockGatheringCompletionCardMutationsResult {
    /** Отпуск резерва КВ. */
    completeReserveMutation: UseMutationResult<void, Error, StockGatheringCompleteReserveParams>;
    /** Временное завершение комплектования: перевод связанных ВЗН СУ. */
    finishComplectMutation: UseMutationResult<StockGatheringFinishComplectResult, Error, StockGatheringFinishComplectParams>;
    /** Выполнение BO-операции над КВ. */
    performBoOperationMutation: UseMutationResult<void, Error, PerformBoOperationVariables>;
}

/** Screen-level мутации карточки комплектования КВ. */
export function useStockGatheringCompletionCardMutations({
    onReserveRefetch,
    onBoOperationSuccess,
}: UseStockGatheringCompletionCardMutationsParams): UseStockGatheringCompletionCardMutationsResult {
    const completeReserveMutation = useMutation({
        mutationKey: ["stock", "gatheringCompletion", "reserve", "complete"],
        meta: { errorSource: "stock.gatheringCompletion.reserve.complete" },
        mutationFn: (variables: StockGatheringCompleteReserveParams) => stockGatheringsServices.reserves.complete(variables),
        onSuccess: async (_data, variables) => {
            notification.success("Резерв отпущен");
            await onReserveRefetch?.({
                reserveCode: variables.reserveCode,
                gatheringContCode: variables.gatheringContCode,
            });
        },
    });

    const finishComplectMutation = useMutation({
        mutationKey: ["stock", "gatheringCompletion", "finishComplect"],
        meta: { errorSource: "stock.gatheringCompletion.finishComplect" },
        mutationFn: (variables: StockGatheringFinishComplectParams) => stockGatheringsServices.finishComplect(variables),
        onSuccess: (result) => {
            if (result.inplantCount === 0) {
                notification.warning("Связанные ВЗН СУ по этой КВ не найдены");
            } else if (result.promotedCount === 0) {
                notification.success("Все связанные ВЗН СУ уже в целевом статусе");
            } else {
                notification.success(`Комплектование завершено: переведено ВЗН — ${result.promotedCount}`);
            }
            onBoOperationSuccess?.();
        },
    });

    const performBoOperationMutation = usePerformBoOperationMutation({
        scope: "stockGatheringCompletion",
        onSuccess: onBoOperationSuccess,
    });

    return {
        completeReserveMutation,
        finishComplectMutation,
        performBoOperationMutation,
    };
}
